/**
 * library.ts — super-user curated Library.
 *
 * A `Library` is a curated COLLECTION (its own title/description/author/
 * thumbnail) that contains many courses, joined via the `library_courses`
 * table. Admins (a row in `app_admins`, managed manually via the Supabase SQL
 * editor — see db/library/01_library_tables.sql) create and manage libraries;
 * everyone can read them. Writes are gated by RLS, not just hidden in the UI.
 *
 * Opening a course inside a library is read-only. Editing clones the course's
 * ROOT-LANGUAGE version into the current user's own account — modules, slides,
 * and narration audio included — and the library / source course are never
 * mutated.
 */

import { supabase } from './supabase'
import { activeOrgId } from './activeOrg'
import { getCourse, getCourseModules, saveCourseModules, getCourses } from './db'
import type { CourseGroup } from './db'
import { generateAudioB64 } from './tts'
import { saveAudio } from './audioCache'
import type { Course, Module, Library } from '@/types'
import type { VoiceGender } from './languages'

/**
 * A course family is eligible for a Library once its ORIGINAL (root) language
 * is published (has a share_code) — that's the version we preview/share/clone.
 * Listed as one entry per course; the row shows how many languages it has.
 */
export function isFamilyLibraryEligible(group: CourseGroup): boolean {
  return !!group.root.share_code
}

export async function isSuperUser(userId: string | null | undefined): Promise<boolean> {
  if (!userId) return false
  const { data, error } = await supabase
    .from('app_admins')
    .select('clerk_user_id')
    .eq('clerk_user_id', userId)
    .maybeSingle()
  if (error) {
    console.error('[Supabase] isSuperUser check failed:', error.message, error)
    return false
  }
  return data != null
}

export async function getLibraries(): Promise<Library[]> {
  const { data, error } = await supabase
    .from('libraries')
    .select('*')
    .order('created_at', { ascending: false })
  if (error) {
    console.error('[Supabase] getLibraries failed:', error.message, error)
    return []
  }
  return (data ?? []) as Library[]
}

export async function getLibrary(id: string): Promise<Library | null> {
  const { data, error } = await supabase
    .from('libraries')
    .select('*')
    .eq('id', id)
    .maybeSingle()
  if (error) {
    console.error('[Supabase] getLibrary failed:', error.message, error)
    return null
  }
  return (data as Library) ?? null
}

/** Course ids in a library, in saved order. */
export async function getLibraryCourseIds(libraryId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('library_courses')
    .select('course_id, position')
    .eq('library_id', libraryId)
    .order('position', { ascending: true })
  if (error) {
    console.error('[Supabase] getLibraryCourseIds failed:', error.message, error)
    return []
  }
  return (data ?? []).map(r => r.course_id as string)
}

/** Map of library id → its course ids, in saved order. One round-trip for all
 *  libraries — used to render catalog/home cards (course count + languages). */
export async function getLibraryCourseMap(): Promise<Map<string, string[]>> {
  const { data, error } = await supabase
    .from('library_courses')
    .select('library_id, course_id, position')
    .order('position', { ascending: true })
  if (error) {
    console.error('[Supabase] getLibraryCourseMap failed:', error.message, error)
    return new Map()
  }
  const map = new Map<string, string[]>()
  for (const r of data ?? []) {
    const arr = map.get(r.library_id as string) ?? []
    arr.push(r.course_id as string)
    map.set(r.library_id as string, arr)
  }
  return map
}

/** Distinct language count across a collection's course families (each course
 *  id is a root; union the languages of its whole translation family). */
export function countLanguages(courseRootIds: string[], groupByRootId: Map<string, CourseGroup>): number {
  const langs = new Set<string>()
  for (const id of courseRootIds) {
    const g = groupByRootId.get(id)
    if (g) for (const c of g.all) langs.add(c.language)
  }
  return langs.size
}

async function writeLibraryCourses(libraryId: string, courseIds: string[]): Promise<boolean> {
  // Replace the whole set: clear, then insert in order. Junction is small.
  const { error: delErr } = await supabase.from('library_courses').delete().eq('library_id', libraryId)
  if (delErr) {
    console.error('[Supabase] writeLibraryCourses delete failed:', delErr.message, delErr)
    return false
  }
  if (courseIds.length === 0) return true
  const rows = courseIds.map((course_id, position) => ({ library_id: libraryId, course_id, position }))
  const { error: insErr } = await supabase.from('library_courses').insert(rows)
  if (insErr) {
    console.error('[Supabase] writeLibraryCourses insert failed:', insErr.message, insErr)
    return false
  }
  return true
}

export async function createLibrary(params: {
  title: string
  description?: string
  authorName?: string
  thumbnailUrl?: string
  courseIds: string[]
  createdBy: string
}): Promise<Library | null> {
  const now = new Date().toISOString()
  const library: Library = {
    id: `lib-${Date.now()}`,
    title: params.title,
    description: params.description,
    author_name: params.authorName,
    thumbnail_url: params.thumbnailUrl,
    created_by: params.createdBy,
    created_at: now,
    updated_at: now,
  }
  const { error } = await supabase.from('libraries').insert(library)
  if (error) {
    console.error('[Supabase] createLibrary failed (likely not an admin):', error.message, error)
    return null
  }
  await writeLibraryCourses(library.id, params.courseIds)
  return library
}

export async function updateLibrary(id: string, params: {
  title: string
  description?: string
  authorName?: string
  thumbnailUrl?: string
  courseIds: string[]
}): Promise<boolean> {
  const { error } = await supabase
    .from('libraries')
    .update({
      title: params.title,
      description: params.description,
      author_name: params.authorName,
      thumbnail_url: params.thumbnailUrl,
      updated_at: new Date().toISOString(),
    })
    .eq('id', id)
  if (error) {
    console.error('[Supabase] updateLibrary failed:', error.message, error)
    return false
  }
  return writeLibraryCourses(id, params.courseIds)
}

export async function deleteLibrary(id: string): Promise<void> {
  // library_courses rows cascade on delete (FK on delete cascade).
  const { error } = await supabase.from('libraries').delete().eq('id', id)
  if (error) console.error('[Supabase] deleteLibrary failed:', error.message, error)
}

/** Mirrors translate.ts's withTtsRetry — retry on rate-limit/server errors only. */
async function withTtsRetry<T>(fn: () => Promise<T>, maxAttempts = 4): Promise<T> {
  let lastErr: unknown
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      return await fn()
    } catch (err) {
      lastErr = err
      const status = (err as { status?: number })?.status
      const retryable = status === 429 || (typeof status === 'number' && status >= 500)
      if (!retryable || attempt === maxAttempts - 1) throw err
      await new Promise(r => setTimeout(r, 500 * 2 ** attempt + Math.random() * 250))
    }
  }
  throw lastErr
}

/**
 * Clone a library course into the given user's own account. A library entry
 * may point at any language version, but we ALWAYS clone the ORIGINAL
 * (root) language — resolve to the root before copying. Produces a fresh
 * standalone course (is_root:true, no root_course_id), deep-copied modules/
 * slides, and freshly generated narration audio for every `_narration`
 * segment (unconditional — the new account's IndexedDB has nothing cached
 * yet). Source is never mutated.
 *
 * `onProgress` reports 0–100 so the caller can drive a notification (mirrors
 * the translation flow).
 */
export interface CloneProgress { step: 'copying' | 'voiceover' | 'done'; progress: number }

export async function cloneCourseForUser(
  courseId: string,
  userId: string,
  onProgress?: (p: CloneProgress) => void,
  libraryId?: string,
): Promise<Course | null> {
  const picked = await getCourse(courseId)
  if (!picked) return null

  // Always clone the original-language root, even if a translated version was
  // the one catalogued.
  const source = picked.is_root ? picked : (await getCourse(picked.root_course_id ?? picked.id)) ?? picked

  const sourceModules = await getCourseModules(source.id)

  const newCourseId = `course-${Date.now()}`
  const now = new Date().toISOString()
  const newCourse: Course = {
    id: newCourseId,
    // "Use this course" copies into the active organisation (null in production, as before).
    org_id: activeOrgId(),
    created_by: userId,
    title: source.title,
    description: source.description,
    thumbnail_url: source.thumbnail_url,
    language: source.language,
    is_root: true,
    status: 'draft',
    pass_mark: source.pass_mark,
    min_questions: source.min_questions,
    default_voice_gender: source.default_voice_gender ?? 'female',
    share_code: null,
    default_narration_skippable: source.default_narration_skippable ?? false,
    default_interact_per_element: source.default_interact_per_element ?? false,
    default_auto_advance: source.default_auto_advance ?? false,
    created_at: now,
    updated_at: now,
  }

  const { error: insertErr } = await supabase.from('courses').insert({
    id: newCourse.id,
    org_id: newCourse.org_id,
    created_by: newCourse.created_by,
    title: newCourse.title,
    description: newCourse.description,
    thumbnail_url: newCourse.thumbnail_url,
    language: newCourse.language,
    is_root: newCourse.is_root,
    status: newCourse.status,
    pass_mark: newCourse.pass_mark,
    min_questions: newCourse.min_questions,
    default_voice_gender: newCourse.default_voice_gender,
    share_code: newCourse.share_code,
    default_narration_skippable: newCourse.default_narration_skippable,
    default_interact_per_element: newCourse.default_interact_per_element,
    default_auto_advance: newCourse.default_auto_advance,
    created_at: newCourse.created_at,
    updated_at: newCourse.updated_at,
  })
  if (insertErr) {
    console.error('[Supabase] cloneCourseForUser: course insert failed:', insertErr.message, insertErr)
    return null
  }

  const defaultGender: VoiceGender = newCourse.default_voice_gender ?? 'female'
  const newModules: Module[] = sourceModules.map((m, mi) => {
    const newModuleId = `mod-${Date.now()}-${mi}`
    return {
      id: newModuleId,
      course_id: newCourseId,
      title: m.title,
      position: mi,
      is_opening: m.is_opening ?? false,
      created_at: now,
      slides: (m.slides ?? []).map((s, si) => ({
        id: `sld-${Date.now()}-${mi}-${si}`,
        module_id: newModuleId,
        format_id: s.format_id,
        position: si,
        content: structuredClone(s.content),
        created_at: now,
        updated_at: now,
      })),
    }
  })

  await saveCourseModules(newCourseId, newModules, userId)
  onProgress?.({ step: 'copying', progress: 20 })

  // Generate voiceover for every narration segment that survived the clone.
  const voiceJobs: { slideId: string; segIdx: number; text: string; gender: VoiceGender }[] = []
  for (const m of newModules) {
    for (const s of (m.slides ?? [])) {
      const c = s.content as { _narration?: unknown; _narrationVoices?: Array<'female' | 'male' | null> }
      const segs = Array.isArray(c._narration) ? (c._narration as string[]) : []
      segs.forEach((text, i) => {
        if (typeof text === 'string' && text.trim()) {
          voiceJobs.push({ slideId: s.id, segIdx: i, text, gender: c._narrationVoices?.[i] ?? defaultGender })
        }
      })
    }
  }
  let vdone = 0
  for (const v of voiceJobs) {
    try {
      const b64 = await withTtsRetry(() => generateAudioB64(v.text, undefined, newCourse.language, v.gender, 'narration'))
      await saveAudio(newCourseId, v.slideId, v.segIdx, b64, v.gender, v.text)
    } catch (err) {
      console.error(`[library] voiceover failed (slide ${v.slideId} seg ${v.segIdx} ${v.gender}) — skipping`, err)
    }
    vdone++
    onProgress?.({ step: 'voiceover', progress: 20 + Math.round((vdone / Math.max(1, voiceJobs.length)) * 75) })
  }

  // Record the clone event (popularity / social proof — surfaced later).
  // Best-effort: never block the clone on logging.
  recordCourseClone(source.id, userId, libraryId)

  // Warm the local course registry (scoped to this user) so EditorPage's
  // synchronous metadata read (getCourseFromIndex) picks up the new course.
  await getCourses(userId)
  onProgress?.({ step: 'done', progress: 100 })

  return newCourse
}

/**
 * Fetch the courses for the given root ids PLUS their translations, regardless
 * of owner. Used to render Library collections / cards for any viewer — relies
 * on the cross-user read policy (db/library/03_library_read_policies.sql).
 * Does NOT touch the local course registry (unlike getCourses).
 */
export async function getCoursesForRoots(rootIds: string[]): Promise<Course[]> {
  if (rootIds.length === 0) return []
  const list = rootIds.join(',')
  const { data, error } = await supabase
    .from('courses')
    .select('*')
    .or(`id.in.(${list}),root_course_id.in.(${list})`)
  if (error) {
    console.error('[Supabase] getCoursesForRoots failed:', error.message, error)
    return []
  }
  return (data ?? []) as Course[]
}

/** Append-only clone log (for popularity counts). Best-effort, fire-and-forget. */
export async function recordCourseClone(courseId: string, userId: string, libraryId?: string): Promise<void> {
  const { error } = await supabase.from('course_clones').insert({
    id: `clone-${Date.now()}`,
    course_id: courseId,
    library_id: libraryId ?? null,
    cloned_by: userId,
  })
  if (error) console.error('[Supabase] recordCourseClone failed:', error.message, error)
}

/** Map of course id → number of times it's been cloned. */
export async function getCloneCounts(): Promise<Map<string, number>> {
  const { data, error } = await supabase.from('course_clones').select('course_id')
  if (error) {
    console.error('[Supabase] getCloneCounts failed:', error.message, error)
    return new Map()
  }
  const counts = new Map<string, number>()
  for (const r of data ?? []) {
    const id = r.course_id as string
    counts.set(id, (counts.get(id) ?? 0) + 1)
  }
  return counts
}

/**
 * Deep link to a library course's share panel:
 * /library/{libraryId}?share={courseId}.
 *
 * The route sits behind ProtectedRoute, so pasting this link signs the
 * recipient in first and returns them to the same URL (see `src/lib/redirect.ts`),
 * where LibraryCollectionPage reopens the share dialog and they can pull the
 * SCORM package themselves.
 */
export function libraryCourseUrl(origin: string, libraryId: string, courseId: string): string {
  return `${origin}/library/${libraryId}?share=${encodeURIComponent(courseId)}`
}
